import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Sparkles, Check, Truck } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/orders/${orderNumber}`); setOrder(data); }
      catch { setOrder(false); }
    })();
  }, [orderNumber]);

  if (order === null) return <div className="p-20 text-center">Loading…</div>;
  if (order === false) return <div className="p-20 text-center">Order not found.</div>;

  return (
    <div data-testid="order-confirmation-container" className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
      <div className="text-center mb-10 fade-up">
        <div className="w-16 h-16 rounded-full bg-candle-terracotta/15 mx-auto mb-4 flex items-center justify-center">
          <Check className="w-8 h-8 text-candle-terracotta" />
        </div>
        <div className="text-xs uppercase tracking-[0.3em] text-candle-terracotta mb-2">Order Confirmed</div>
        <h1 className="font-serif text-4xl sm:text-5xl">Your Glow Is On Its Way <Sparkles className="inline w-8 h-8 text-candle-gold" /></h1>
        <p className="text-muted-foreground mt-3">Thanks {order.address.fullName.split(" ")[0]}! A confirmation has been prepared for {order.address.email}.</p>
      </div>

      <div className="p-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] space-y-5 fade-up fade-up-delay-1">
        <div className="flex flex-wrap items-center justify-between gap-2 pb-4 border-b border-[hsl(var(--border))]">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Order Number</div>
            <div data-testid="order-number-display" className="font-mono text-lg">#{order.orderNumber}</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Estimated Delivery</div>
            <div className="font-medium flex items-center gap-1"><Truck className="w-4 h-4" /> {order.estimatedDelivery}</div>
          </div>
        </div>

        <div className="space-y-3">
          {order.items.map((it, i) => (
            <div key={i} className="flex gap-3 items-center">
              <img src={it.image} alt={it.name} className="w-14 h-14 rounded-lg object-cover" />
              <div className="flex-1">
                <div className="font-medium">{it.name}</div>
                <div className="text-xs text-muted-foreground">Qty {it.quantity} · {formatINR(it.unitPrice)}</div>
              </div>
              <div className="font-medium">{formatINR(it.unitPrice * it.quantity)}</div>
            </div>
          ))}
        </div>

        <div className="border-t border-[hsl(var(--border))] pt-4 space-y-1 text-sm">
          <Row label="Subtotal" value={formatINR(order.subtotal)} />
          {order.discount > 0 && <Row label={`Discount ${order.couponCode}`} value={`−${formatINR(order.discount)}`} accent />}
          <Row label="Tax" value={formatINR(order.tax)} />
          <Row label="Shipping" value={order.shipping === 0 ? "Free" : formatINR(order.shipping)} />
          <div className="flex justify-between pt-2 border-t border-[hsl(var(--border))] font-serif text-xl">
            <span>Total</span><span>{formatINR(order.total)}</span>
          </div>
        </div>

        <div className="border-t border-[hsl(var(--border))] pt-4 grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Shipping to</div>
            <div>{order.address.fullName}<br />{order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}<br />{order.address.city}, {order.address.state} {order.address.pinCode}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Payment</div>
            <div className="capitalize">{order.paymentMethod === "cod" ? "Cash on Delivery" : order.paymentMethod}</div>
          </div>
        </div>
      </div>

      <div className="flex gap-3 mt-8 justify-center">
        <Link to="/shop"><Button className="rounded-full">Continue Shopping</Button></Link>
        <Link to="/"><Button variant="outline" className="rounded-full">Back to Home</Button></Link>
      </div>
    </div>
  );
}

const Row = ({ label, value, accent }) => (
  <div className="flex justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className={accent ? "text-candle-terracotta font-medium" : "font-medium"}>{value}</span>
  </div>
);
